// src/service-config/env.ts

/**
 * Type-safe environment configuration
 *
 * HOW TO ADD NEW ENVIRONMENT VARIABLES:
 * 1. Add to .env file (e.g., VITE_NEW_VAR=value)
 * 2. Add to this file in the env object
 * 3. That's it! TypeScript will infer the type automatically
 */

// Helper: Parse comma-separated string to array
export const parseArray = (value: string): string[] => {
  return value.split(',').map((item) => item.trim());
};

// Helper: Parse string to number
export const parseNumber = (value: string, varName: string): number => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    throw new Error(`[ENV] Invalid ${varName}: "${value}" is not a number`);
  }
  return parsed;
};

// Helper: Parse string to boolean (for future use)
export const parseBoolean = (value: string): boolean => {
  return value === 'true' || value === '1';
};

// Helper: Validate required variable exists
export const getRequired = (key: string, varName: string): string => {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`[ENV] Missing required environment variable: ${varName}`);
  }
  return value;
};

/**
 * Reactive mode configuration
 * These values are fetched from the backend on app initialization
 */
class ModeConfig {
  private _isManaged = false;
  private _isStandalone = false;
  private _licenseMode: 'online' | 'offline' | null = null;

  get isManaged(): boolean {
    return this._isManaged;
  }

  get isStandalone(): boolean {
    return this._isStandalone;
  }

  get licenseMode(): 'online' | 'offline' | null {
    return this._licenseMode;
  }

  get isLicenseOnline(): boolean {
    return this._licenseMode === 'online';
  }

  get isLicenseOffline(): boolean {
    return this._licenseMode === 'offline';
  }

  updateFromBackend(config: { mode: { isManaged: boolean; isStandalone: boolean }; licenseMode?: { mode: 'online' | 'offline' } }) {
    this._isManaged = config.mode.isManaged;
    this._isStandalone = config.mode.isStandalone;
    this._licenseMode = config.licenseMode?.mode || null;

    if (import.meta.env.DEV) {
      console.log('[ENV] Mode configuration updated from backend:', {
        isManaged: this._isManaged,
        isStandalone: this._isStandalone,
        licenseMode: this._licenseMode
      });
    }
  }
}

const modeConfig = new ModeConfig();

/**
 * Environment Configuration
 * All environment variables in one place, properly typed and validated
 */
export const env = {
  //App Mode - Reactive values fetched from backend (initial: false)
  get isStandalone() {
    return modeConfig.isStandalone;
  },
  get isManaged() {
    return modeConfig.isManaged;
  },
  get licenseMode() {
    return modeConfig.licenseMode;
  },
  get isLicenseOnline() {
    return modeConfig.isLicenseOnline;
  },
  get isLicenseOffline() {
    return modeConfig.isLicenseOffline;
  },

  // API Configuration
  apiBaseUrl: getRequired('VITE_API_BASE_URL', 'VITE_API_BASE_URL'),
  masterApiUrl: getRequired('VITE_MASTER_API_URL', 'VITE_MASTER_API_URL'),

  // App Information
  app: {
    name: getRequired('VITE_APP_NAME', 'VITE_APP_NAME'),
    version: getRequired('VITE_APP_VERSION', 'VITE_APP_VERSION'),
  },

  // File Upload Configuration
  fileUpload: {
    supportedTypes: parseArray(getRequired('VITE_SUPPORTED_FILE_TYPES', 'VITE_SUPPORTED_FILE_TYPES')),
    maxFileSizeMB: parseNumber(
      getRequired('VITE_MAX_FILE_SIZE_MB', 'VITE_MAX_FILE_SIZE_MB'),
      'VITE_MAX_FILE_SIZE_MB'
    ),
    pollingIntervalMs: parseNumber(
      getRequired('VITE_POLLING_INTERVAL_MS', 'VITE_POLLING_INTERVAL_MS'),
      'VITE_POLLING_INTERVAL_MS'
    ),
    maxPollsMinutes: parseNumber(
      getRequired('VITE_MAX_POLLS_MINUTES', 'VITE_MAX_POLLS_MINUTES'),
      'VITE_MAX_POLLS_MINUTES'
    ),
  },

  // Runtime flags
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,

  // Internal method to update mode config from backend
  _updateModeConfig: (config: Parameters<typeof modeConfig.updateFromBackend>[0]) => {
    modeConfig.updateFromBackend(config);
  }
};

// Computed values (derived from env vars)
export const computed = {
  // Max file size in bytes
  maxFileSizeBytes: env.fileUpload.maxFileSizeMB * 1024 * 1024,

  // Maximum number of polls before timeout
  maxPolls: Math.ceil(
    (env.fileUpload.maxPollsMinutes * 60 * 1000) / env.fileUpload.pollingIntervalMs
  ),
} as const;

// Utility functions
export const isFileTypeSupported = (fileType: string): boolean => {
  const normalizedType = fileType.toLowerCase().replace('.', '');
  return env.fileUpload.supportedTypes.includes(normalizedType);
};

export const isFileSizeValid = (fileSizeBytes: number): boolean => {
  return fileSizeBytes <= computed.maxFileSizeBytes;
};

// Export types for use in components
export type Env = typeof env;
export type SupportedFileType = (typeof env.fileUpload.supportedTypes)[number];

// Log configuration in development
if (env.isDevelopment) {
  console.log('[ENV] Configuration loaded:', {
    apiBaseUrl: env.apiBaseUrl,
    appName: env.app.name,
    maxFileSize: `${env.fileUpload.maxFileSizeMB}MB`,
    supportedTypes: env.fileUpload.supportedTypes.join(', '),
  });
}